// Puerto de packages/hce/src/hce/paciente/DocumentosTab.tsx. "Subir
// archivo" (Rediseño mayor 2026-09-02, Fase 2 "destinos reales del nav")
// suma un documento genérico vía SubirDocumento.tsx, aparte del flujo de
// formularios estructurados que ya existía ("Nuevo documento"). Si llega
// por Accesos Rápidos ("Subir resultado" → PacientesPage.tsx →
// `?subir=1`), el diálogo se abre solo. useSearchResources no expone un
// refetch (su 3er valor es un OperationOutcome, no una función) — mismo
// patrón manual que ya usa AlergiasTab.tsx (useAlergiasConRefresh) para
// poder refrescar la lista después de subir.
import type { MedplumClient } from '@medplum/core';
import { useMedplum } from '@medplum/react-hooks';
import type { DocumentReference } from '@medplum/fhirtypes';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { notify } from '@/components/ui/sonner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { SubirDocumento } from '@/formulario/SubirDocumento';
import { SelectorFormularios } from './SelectorFormularios';
import { TablaVacia } from './TablaVacia';

/**
 * Abre el adjunto (PDF firmado o archivo subido con "Subir documento") en una
 * pestaña nueva — nunca descarga automática, el médico decide si la abre.
 * Bug real reportado en vivo 2026-09-05: la tabla de Documentos mostraba
 * fecha/nombre/estado en texto plano, sin ninguna forma de leer el contenido
 * ya guardado (mismo patrón de "no se puede leer" que Notas/Historia).
 */
async function verDocumento(medplum: MedplumClient, doc: DocumentReference, mensajeSinAdjunto: string): Promise<void> {
  const url = doc.content?.[0]?.attachment?.url;
  if (!url) {
    notify({ color: 'red', message: mensajeSinAdjunto });
    return;
  }
  try {
    const blob = await medplum.download(url);
    const objectUrl = URL.createObjectURL(blob);
    window.open(objectUrl, '_blank');
  } catch (err) {
    notify({ color: 'red', message: err instanceof Error ? err.message : String(err) });
  }
}

function useDocumentosConRefresh(patientId: string) {
  const medplum = useMedplum();
  const [version, setVersion] = useState(0);
  const [documentos, setDocumentos] = useState<DocumentReference[] | undefined>(undefined);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    let cancelado = false;
    setCargando(true);
    medplum
      .searchResources('DocumentReference', { patient: `Patient/${patientId}`, _sort: '-date', _count: 100 })
      .then((r) => {
        if (!cancelado) {
          setDocumentos(r as DocumentReference[]);
          setCargando(false);
        }
      })
      .catch(() => {
        if (!cancelado) setCargando(false);
      });
    return () => {
      cancelado = true;
    };
  }, [medplum, patientId, version]);

  return [documentos, cargando, () => setVersion((v) => v + 1)] as const;
}

export function DocumentosTab(): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const { id } = useParams() as { id: string };
  const [searchParams, setSearchParams] = useSearchParams();
  const [documentos, cargando, refrescar] = useDocumentosConRefresh(id);
  const [abierto, setAbierto] = useState(false);
  const [subirAbierto, setSubirAbierto] = useState(searchParams.get('subir') === '1');
  const [descartando, setDescartando] = useState<string | null>(null);

  const lista = documentos ?? [];

  // Descartar (2026-09-11, pedido explícito) — un DocumentReference "current"
  // roto/duplicado/subido por error no tenía ninguna forma de sacarse de en
  // medio. `entered-in-error` es el estado FHIR correcto para "esto no debe
  // contarse como válido" (no existe un intermedio tipo "cancelado" para
  // DocumentReference) — nunca se borra el recurso, mismo criterio que el
  // resto del proyecto.
  async function descartar(doc: DocumentReference): Promise<void> {
    if (!doc.id) return;
    setDescartando(doc.id);
    try {
      await medplum.updateResource<DocumentReference>({ ...doc, status: 'entered-in-error' });
      refrescar();
    } catch (err) {
      notify({ color: 'red', message: err instanceof Error ? err.message : String(err) });
    } finally {
      setDescartando(null);
    }
  }

  function cerrarSubir(): void {
    setSubirAbierto(false);
    if (searchParams.get('subir')) {
      searchParams.delete('subir');
      setSearchParams(searchParams, { replace: true });
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <h5 className="text-sm font-semibold">{t('fichaPaciente.tabs.documentos')}</h5>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setSubirAbierto(true)}>
            {t('subirDocumento.botonAbrir')}
          </Button>
          <Button size="sm" variant={abierto ? 'secondary' : 'default'} onClick={() => setAbierto((v) => !v)}>
            {abierto ? t('fichaPaciente.notas.cerrarNuevaNota') : t('fichaPaciente.notas.nuevoDocumento')}
          </Button>
        </div>
      </div>
      <Collapsible open={abierto}>
        <CollapsibleContent>
          <SelectorFormularios subject={`Patient/${id}`} soloGrupo="documentos" />
        </CollapsibleContent>
      </Collapsible>

      <Dialog open={subirAbierto} onOpenChange={(v) => (v ? setSubirAbierto(true) : cerrarSubir())}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('subirDocumento.botonAbrir')}</DialogTitle>
          </DialogHeader>
          <SubirDocumento
            subject={{ reference: `Patient/${id}` }}
            onSubido={() => {
              refrescar();
              cerrarSubir();
            }}
          />
        </DialogContent>
      </Dialog>

      {cargando ? (
        <p className="text-sm text-muted-foreground">{t('fichaPaciente.cargando')}</p>
      ) : lista.length === 0 ? (
        <TablaVacia mensaje={t('fichaPaciente.sinDatos')} />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('fichaPaciente.columnas.fecha')}</TableHead>
              <TableHead>{t('fichaPaciente.columnas.documento')}</TableHead>
              <TableHead>{t('fichaPaciente.columnas.estado')}</TableHead>
              <TableHead>{t('fichaPaciente.columnas.acciones')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {lista.map((d) => {
              const nombre = d.description ?? d.type?.text ?? d.category?.[0]?.text ?? '—';
              const tieneAdjunto = Boolean(d.content?.[0]?.attachment?.url);
              return (
                <TableRow key={d.id}>
                  <TableCell>{d.date ? new Date(d.date).toLocaleString('es-EC') : '—'}</TableCell>
                  <TableCell>
                    {tieneAdjunto ? (
                      <button
                        type="button"
                        className="text-sm text-hce-pine underline-offset-2 hover:underline"
                        onClick={() => void verDocumento(medplum, d, t('fichaPaciente.documentoSinAdjunto'))}
                      >
                        {nombre}
                      </button>
                    ) : (
                      nombre
                    )}
                  </TableCell>
                  <TableCell>{d.status ? t(`fichaPaciente.estadoDocumento.${d.status}`, d.status) : '—'}</TableCell>
                  <TableCell>
                    {d.status === 'current' && (
                      <Button variant="ghost" size="sm" disabled={descartando === d.id} onClick={() => void descartar(d)}>
                        {descartando === d.id ? t('fichaPaciente.descartando') : t('fichaPaciente.descartar')}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
